import { z } from 'zod';

export const COMPLETION_MODEL_CLASSES = ['fast', 'balanced', 'capable', 'reasoning'] as const;
export const DECISION_MODEL_CLASSES = ['decision'] as const;
export const MODEL_CLASSES = [...COMPLETION_MODEL_CLASSES, ...DECISION_MODEL_CLASSES] as const;

export type ModelClass = z.infer<typeof SchemaModelClass>;
export const SchemaModelClass = z.enum(MODEL_CLASSES);

export type CompletionModelClass = z.infer<typeof SchemaCompletionModelClass>;
export const SchemaCompletionModelClass = z.enum(COMPLETION_MODEL_CLASSES);

export type DecisionModelClass = z.infer<typeof SchemaDecisionModelClass>;
export const SchemaDecisionModelClass = z.enum(DECISION_MODEL_CLASSES);

export const COMPLETION_TOOL_NAME_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
export const MAX_COMPLETION_TOOLS = 128;

export type CompletionTextPart = z.infer<typeof SchemaCompletionTextPart>;
export const SchemaCompletionTextPart = z.strictObject({
    type: z.literal('text'),
    text: z.string(),
});

export type CompletionReasoningPart = z.infer<typeof SchemaCompletionReasoningPart>;
export const SchemaCompletionReasoningPart = z.strictObject({
    type: z.literal('reasoning'),
    text: z.string(),
    providerOptions: z.strictObject({ lumi: z.strictObject({ reasoning: z.string().min(1) }) }),
});

export type CompletionToolCallPart = z.infer<typeof SchemaCompletionToolCallPart>;
export const SchemaCompletionToolCallPart = z.strictObject({
    type: z.literal('tool-call'),
    toolCallId: z.string().min(1),
    toolName: z.string().min(1),
    input: z.unknown(),
});

export type CompletionToolResultOutput = z.infer<typeof SchemaCompletionToolResultOutput>;
export const SchemaCompletionToolResultOutput = z.discriminatedUnion('type', [
    z.strictObject({ type: z.literal('text'), value: z.string() }),
    z.strictObject({ type: z.literal('json'), value: z.unknown() }),
    z.strictObject({ type: z.literal('error-text'), value: z.string() }),
    z.strictObject({ type: z.literal('error-json'), value: z.unknown() }),
]);

export type CompletionToolResultPart = z.infer<typeof SchemaCompletionToolResultPart>;
export const SchemaCompletionToolResultPart = z.strictObject({
    type: z.literal('tool-result'),
    toolCallId: z.string().min(1),
    toolName: z.string().min(1),
    output: SchemaCompletionToolResultOutput,
});

export type CompletionAssistantPart = z.infer<typeof SchemaCompletionAssistantPart>;
export const SchemaCompletionAssistantPart = z.discriminatedUnion('type', [
    SchemaCompletionTextPart,
    SchemaCompletionReasoningPart,
    SchemaCompletionToolCallPart,
]);

export type CompletionMessage = z.infer<typeof SchemaCompletionMessage>;
export const SchemaCompletionMessage = z.discriminatedUnion('role', [
    z.strictObject({
        role: z.literal('user'),
        content: z.union([z.string(), z.array(SchemaCompletionTextPart).min(1)]),
    }),
    z.strictObject({
        role: z.literal('assistant'),
        content: z.union([z.string(), z.array(SchemaCompletionAssistantPart).min(1)]),
    }),
    z.strictObject({
        role: z.literal('tool'),
        content: z.array(SchemaCompletionToolResultPart).min(1),
    }),
]);

export type CompletionTool = z.infer<typeof SchemaCompletionTool>;
export const SchemaCompletionTool = z.strictObject({
    description: z.string().optional(),
    inputSchema: z.record(z.string(), z.unknown()),
});

export type CompletionToolChoice = z.infer<typeof SchemaCompletionToolChoice>;
export const SchemaCompletionToolChoice = z.union([
    z.enum(['auto', 'required', 'none']),
    z.strictObject({ type: z.literal('tool'), toolName: z.string().regex(COMPLETION_TOOL_NAME_PATTERN) }),
]);

export type CompletionRequest = z.infer<typeof SchemaCompletionRequest>;
export const SchemaCompletionRequest = z
    .strictObject({
        model: SchemaCompletionModelClass,
        instructions: z.string().optional(),
        prompt: z.union([z.string().min(1), z.array(SchemaCompletionMessage).min(1)]),
        schema: z.record(z.string(), z.unknown()).optional(),
        maxOutputTokens: z.int().min(16).optional(),
        tools: z
            .record(z.string().regex(COMPLETION_TOOL_NAME_PATTERN), SchemaCompletionTool)
            .refine((tools) => {
                const count = Object.keys(tools).length;
                return count >= 1 && count <= MAX_COMPLETION_TOOLS;
            }, `offer 1 to ${MAX_COMPLETION_TOOLS} tools`)
            .optional(),
        toolChoice: SchemaCompletionToolChoice.optional(),
        parallelToolCalls: z.boolean().optional(),
    })
    .superRefine((request, ctx) => {
        if (request.tools === undefined) {
            for (const field of ['toolChoice', 'parallelToolCalls'] as const) {
                if (request[field] !== undefined) {
                    ctx.addIssue({ code: 'custom', path: [field], message: `${field} needs tools` });
                }
            }
            return;
        }
        const choice = request.toolChoice;
        if (typeof choice === 'object' && !Object.hasOwn(request.tools, choice.toolName)) {
            ctx.addIssue({
                code: 'custom',
                path: ['toolChoice', 'toolName'],
                message: 'toolChoice names a tool that is not in tools',
            });
        }
    });

export type CompletionUsage = z.infer<typeof SchemaCompletionUsage>;
export const SchemaCompletionUsage = z.strictObject({
    inputTokens: z.int().nonnegative(),
    outputTokens: z.int().nonnegative(),
    cachedInputTokens: z.int().nonnegative().optional(),
    reasoningTokens: z.int().nonnegative().optional(),
    totalTokens: z.int().nonnegative(),
});

export type CompletionToolCall = z.infer<typeof SchemaCompletionToolCall>;
export const SchemaCompletionToolCall = z.strictObject({
    toolCallId: z.string(),
    toolName: z.string(),
    input: z.unknown(),
    invalid: z.literal(true).optional(),
    error: z.string().optional(),
});

export type CompletionResult = z.infer<typeof SchemaCompletionResult>;
export const SchemaCompletionResult = z.strictObject({
    text: z.string(),
    output: z.unknown().optional(),
    finishReason: z.enum(['stop', 'length', 'tool-calls']),
    toolCalls: z.array(SchemaCompletionToolCall).optional(),
    response: z.strictObject({ messages: z.array(SchemaCompletionMessage) }).optional(),
    usage: SchemaCompletionUsage,
    model: SchemaCompletionModelClass,
    requestId: z.string(),
});

export type CompletionErrorBody = z.infer<typeof SchemaCompletionErrorBody>;
export const SchemaCompletionErrorBody = z.strictObject({
    error: z.string(),
    hint: z.string().optional(),
    status: z.int().optional(),
});

export const COMPLETION_ERROR_STATUS = {
    invalid_request: 400,
    llm_not_declared: 400,
    model_not_declared: 400,
    not_found: 404,
    content_filtered: 422,
    rate_limited: 429,
    cap_exhausted: 429,
    invalid_output: 500,
    output_truncated: 500,
    internal_error: 500,
    completion_failed: 502,
    assistant_failed: 502,
    not_available: 503,
    completion_timeout: 504,
    assistant_timeout: 504,
} as const;

export type CompletionErrorCode = keyof typeof COMPLETION_ERROR_STATUS;

export type DecisionState = z.infer<typeof SchemaDecisionState>;
export const SchemaDecisionState = z.union([z.string().min(1), z.record(z.string(), z.unknown())]);

export type DecisionQuestion = z.infer<typeof SchemaDecisionQuestion>;
export const SchemaDecisionQuestion = z.discriminatedUnion('type', [
    z.strictObject({
        type: z.literal('noul'),
        instructions: z.string().min(1),
        criteria: z.strictObject({ true: z.string().optional(), false: z.string().optional() }).optional(),
    }),
    z.strictObject({
        type: z.literal('choice'),
        instructions: z.string().min(1),
        criteria: z.record(z.string().min(1), z.string()).refine((labels) => {
            const count = Object.keys(labels).length;
            return count >= 2 && count <= 255;
        }, 'a choice Question takes 2 to 255 labels'),
    }),
    z.strictObject({
        type: z.literal('score'),
        instructions: z.string().min(1),
        criteria: z.array(z.string().min(1)).min(2).max(10),
    }),
]);

export type DecisionRequest = z.infer<typeof SchemaDecisionRequest>;
export const SchemaDecisionRequest = z.strictObject({
    model: SchemaDecisionModelClass,
    state: SchemaDecisionState,
    questions: z
        .record(z.string().min(1), SchemaDecisionQuestion)
        .refine((questions) => Object.keys(questions).length > 0, 'ask at least one Question'),
});

export type DecisionAnswer = z.infer<typeof SchemaDecisionAnswer>;
export const SchemaDecisionAnswer = z.discriminatedUnion('type', [
    z.object({ type: z.literal('noul'), noul: z.number() }),
    z.object({
        type: z.literal('choice'),
        choice: z.string(),
        confidence: z.number(),
        probabilities: z.record(z.string(), z.number()),
    }),
    z.object({
        type: z.literal('score'),
        score: z.number(),
        confidence: z.number(),
        probabilities: z.record(z.string(), z.number()),
        legend: z.record(z.string(), z.string()),
    }),
]);

export type DecisionUsage = z.infer<typeof SchemaDecisionUsage>;
export const SchemaDecisionUsage = z.strictObject({
    inputTokens: z.int().nonnegative(),
    outputTokens: z.int().nonnegative(),
    totalTokens: z.int().nonnegative(),
});

export type DecisionResult = z.infer<typeof SchemaDecisionResult>;
export const SchemaDecisionResult = z.strictObject({
    answers: z.record(z.string(), SchemaDecisionAnswer),
    usage: SchemaDecisionUsage,
    model: SchemaDecisionModelClass,
    requestId: z.string(),
});

export type DecisionErrorBody = CompletionErrorBody;
export const SchemaDecisionErrorBody = SchemaCompletionErrorBody;

export const DECISION_ERROR_STATUS = {
    invalid_request: 400,
    llm_not_declared: 400,
    model_not_declared: 400,
    not_found: 404,
    content_filtered: 422,
    rate_limited: 429,
    cap_exhausted: 429,
    internal_error: 500,
    decision_failed: 502,
    not_available: 503,
    decision_timeout: 504,
} as const;

export type DecisionErrorCode = keyof typeof DECISION_ERROR_STATUS;
